/* ------------------------------------------------------------------
   HAUS Terrazzo content sections behaviour.

   One tab/panel switcher, reused by two moments on the page: the resealing
   situation switcher in haus-terrazzo-care ([data-tc-seal]) and the
   "why it goes here" surface switcher in haus-terrazzo-places
   ([data-tp-switch]). Same markup contract ([data-tc-tab] / [data-tc-panel]
   inside), same behaviour, same look, so the page reads as one interaction
   language rather than two different widgets. Click and keyboard driven,
   no scroll listeners anywhere (CLAUDE.md non-negotiable #9).

   Idempotent via window.HausTerrazzoContent, and re-inits per section on
   shopify:section:load so the theme editor stays live.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  if (window.HausTerrazzoContent) return;
  window.HausTerrazzoContent = true;

  function initTabSwitch(root) {
    if (!root || root.dataset.tcInit === '1') return;
    root.dataset.tcInit = '1';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tc-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-tc-panel]'));
    if (!tabs.length || !panels.length) return;

    function select(index, focusTab) {
      tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.classList.toggle('is-on', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      });
      panels.forEach(function (panel, i) {
        var on = i === index;
        panel.classList.toggle('is-on', on);
        if (on) { panel.removeAttribute('hidden'); } else { panel.setAttribute('hidden', ''); }
      });
      if (focusTab && tabs[index]) tabs[index].focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i, false); });
      tab.addEventListener('keydown', function (e) {
        var last = tabs.length - 1;
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = last;
        if (next !== null) { e.preventDefault(); select(next, true); }
      });
    });
  }

  /* The as-cast / ground reveal, if a story section is ever added back.
     Kept generic so it initialises only when the markup is present. */
  function initReveal(root) {
    if (!root || root.dataset.tmInit === '1') return;
    root.dataset.tmInit = '1';

    var handle = root.querySelector('[data-tm-handle]');
    if (!handle) return;
    var dragging = false;

    function setPos(pct) {
      var v = Math.max(0, Math.min(100, pct));
      root.style.setProperty('--tm-pos', v + '%');
      handle.setAttribute('aria-valuenow', Math.round(v));
    }

    function fromClientX(x) {
      var r = root.getBoundingClientRect();
      if (!r.width) return;
      setPos(((x - r.left) / r.width) * 100);
    }

    root.addEventListener('pointerdown', function (e) {
      dragging = true;
      if (root.setPointerCapture && e.pointerId !== undefined) {
        try { root.setPointerCapture(e.pointerId); } catch (err) {}
      }
      fromClientX(e.clientX);
    });
    root.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      e.preventDefault();
      fromClientX(e.clientX);
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
      root.addEventListener(evt, function () { dragging = false; });
    });

    handle.addEventListener('keydown', function (e) {
      var now = parseFloat(handle.getAttribute('aria-valuenow')) || 50;
      var step = e.shiftKey ? 10 : 4;
      if (e.key === 'ArrowRight') { e.preventDefault(); setPos(now + step); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setPos(now - step); }
      else if (e.key === 'Home') { e.preventDefault(); setPos(0); }
      else if (e.key === 'End') { e.preventDefault(); setPos(100); }
    });
  }

  /* Recipe Fingerprint's card row is always a horizontal scroller (see
     haus-terrazzo-content.css). This sets the initial scroll position once,
     after layout, so the row opens with the second card (Dorothy) flush at
     the left and the first card (Wicked) peeking as a faded hint that the
     row scrolls both ways, rather than opening scrolled all the way left
     with nothing to suggest it scrolls at all. It runs once on load per
     section and never again: this is initial positioning, not a scroll
     listener, so it stays inside CLAUDE.md's non-negotiable "no scroll
     listeners, ever." */
  function initFingerprintCenter(root) {
    if (!root || root.dataset.rfInit === '1') return;
    root.dataset.rfInit = '1';

    function center() {
      var cards = root.querySelectorAll(':scope > *');
      if (cards.length < 2) return;
      var peek = 40;
      var rootRect = root.getBoundingClientRect();
      var secondRect = cards[1].getBoundingClientRect();
      var secondLeft = secondRect.left - rootRect.left + root.scrollLeft;
      var target = secondLeft - peek;
      var maxScroll = root.scrollWidth - root.clientWidth;
      root.scrollLeft = Math.max(0, Math.min(target, maxScroll));
    }

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () { window.requestAnimationFrame(center); });
    } else {
      center();
    }
  }

  function initAll(scope) {
    var s = scope || document;
    Array.prototype.forEach.call(s.querySelectorAll('[data-tc-seal], [data-tp-switch]'), initTabSwitch);
    Array.prototype.forEach.call(s.querySelectorAll('[data-tm-reveal]'), initReveal);
    Array.prototype.forEach.call(s.querySelectorAll('[data-rf-scroller]'), initFingerprintCenter);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
